import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { RadioButtonInput } from '@app/app/_components/Input/RadioButtonInput';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { InsuranceDuration } from '@app/domain/model/eshop/transport/InsuranceDuration';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    totalPrice: number;
    priceToPayNow: number;
    formState: FormState<PurchaseTransportInsuranceResult>;
    paymentFrequency: number;
    onPaymentFrequencyChange?: (paymentFrequency: number) => void;
}

export function PaymentFrequencyPanel({
    totalPrice,
    priceToPayNow,
    formState,
    paymentFrequency,
    onPaymentFrequencyChange,
}: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();

    const handlePaymentFrequencyChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newPaymentFrequency = parseInt(e.target.value);
            onPaymentFrequencyChange?.(newPaymentFrequency);
        },
        [onPaymentFrequencyChange],
    );

    return (
        <CollapsablePanel label={t('eshop.transport.paymentFrequencyPerYear')} collapsable={false}>
            <div className="flex flex-col gap-6 px-6 py-2">
                <RadioButtonInput
                    name="paymentCount"
                    onChange={handlePaymentFrequencyChange}
                    label={t('eshop.general.numberOfPaymentsLabel')}
                    error={formState.violations?.paymentCount}
                    defaultValue={paymentFrequency.toString()}
                    choices={[
                        {
                            value: InsuranceDuration.OneMonth,
                            label: t('eshop.general.singlePayment'),
                        },
                        {
                            value: InsuranceDuration.TwoMonths,
                            label: t('eshop.general.twoPayments'),
                        },
                        {
                            value: InsuranceDuration.FourMonths,
                            label: t('eshop.general.fourPayments'),
                        },
                        {
                            value: InsuranceDuration.SixMonths,
                            label: t('eshop.general.sixPayments'),
                        },
                        {
                            value: InsuranceDuration.TwelveMonths,
                            label: t('eshop.general.TwelvePayments'),
                        },
                    ]}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ReadOnlyField label={t('eshop.transport.totalToPayNowLabel')}>
                        {formatEuroValue(priceToPayNow, tenant)}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.transport.totalForTwelveMonthsLabel')}>
                        {formatEuroValue(totalPrice, tenant)}
                    </ReadOnlyField>
                </div>
            </div>
        </CollapsablePanel>
    );
}
